import { useEffect, useState } from 'react';
import { Send } from 'lucide-react';
import { AddDestinationButtons } from './AddDestinationButtons';
import { DestinationList, type DestinationAction } from './DestinationRow';
import { DestinationSheet } from './DestinationSheet';
import { RemoveDestinationDialog } from './RemoveDestinationDialog';
import {
  addableKinds,
  isAddable,
  isProductKind,
  isStorageKind,
  offeredList,
} from './destination-kinds';
import { takeDestinationRequest } from './setup-request';
import { useDestinationRemoval } from './use-destination-removal';
import type {
  PaigeDestination,
  PaigeDestinationKind,
  PaigeDestinationsBridge,
} from './paige.global';

/** Which sheet is open: adding a kind, or editing an existing destination. */
type Sheet = { kind: PaigeDestinationKind; existing?: PaigeDestination } | null;

/**
 * Where the job sends results, in the job setup's Export section: Download always, then each
 * destination with its health, last delivery, and the edit, test, retry and remove actions. Adding or
 * editing opens a sheet (DestinationSheet); Remove, on the row or in the sheet, asks first
 * (RemoveDestinationDialog). Renders nothing when the workbench is not on a Paige job.
 */
export function SendResultsCard() {
  const bridge = window.paigeWorkbench?.destinations;
  const { rows, note, setNote, reload, act } = useDestinations(bridge);
  const kinds = useOfferedKinds(bridge);
  const { sheet, setSheet, open, saved, closed } = useSheet(rows, reload);
  const removing = useDestinationRemoval(bridge, setNote, reload);
  if (!bridge || rows === null) return null;
  const onAct = (action: DestinationAction, row: PaigeDestination) => {
    if (action === 'edit') open(row);
    else void (action === 'remove' ? removing.ask(row) : act(action, row));
  };
  return (
    <div className="setup-map-setting paige-send-results">
      <Send size={22} aria-hidden="true" />
      <span>
        <strong>Send results to</strong>
        <small>{summaryLine(rows, kinds)}</small>
        <DestinationList rows={rows} onOpen={open} onAct={onAct} />
        <AddDestinationButtons kinds={kinds} onAdd={(kind) => setSheet({ kind })} />
        <small>{note ?? explanation}</small>
      </span>
      {sheet && (
        <DestinationSheet
          key={sheet.existing?.id ?? `new-${sheet.kind}`}
          {...sheet}
          bridge={bridge}
          onClose={closed}
          onSaved={saved}
          onRemove={(row) => (setSheet(null), void removing.ask(row))}
        />
      )}
      <RemovalQuestion removing={removing} />
    </div>
  );
}

/** Remove's question while one is asked. */
function RemovalQuestion({ removing }: { removing: ReturnType<typeof useDestinationRemoval> }) {
  if (!removing.removal) return null;
  return (
    <RemoveDestinationDialog
      {...removing.removal}
      busy={removing.busy}
      error={removing.error}
      onCancel={removing.cancel}
      onConfirm={(sendTo) => void removing.confirm(sendTo)}
    />
  );
}

/** Which sheet is open, and what opening, saving in and closing it do to the card. */
function useSheet(rows: PaigeDestination[] | null, reload: () => void) {
  const [sheet, setSheet] = useState<Sheet>(null);
  useRequestedSheet(rows, setSheet);
  const open = (existing: PaigeDestination) =>
    isAddable(existing.kind) && setSheet({ kind: existing.kind, existing });
  // A storage destination just added stays open, now stored, to choose its folder once connected; a
  // Content Central or Nucleus One one, added with its key, to choose its catalog or project.
  const saved = (stored?: PaigeDestination) => {
    setSheet(
      stored && !sheet?.existing && (isStorageKind(stored.kind) || isProductKind(stored.kind))
        ? { kind: stored.kind, existing: stored }
        : null,
    );
    reload();
  };
  // A test saves a storage destination's sheet first, so the rows may have changed on any close.
  const closed = () => (setSheet(null), reload());
  return { sheet, setSheet, open, saved, closed };
}

/**
 * The destination the job's setup was opened for (`?setup=export&destination=`, back from a storage
 * sign-in): its sheet opens once the rows are read. Taken once; an id the job does not have is dropped.
 */
function useRequestedSheet(
  rows: PaigeDestination[] | null,
  setSheet: (sheet: Sheet) => void,
): void {
  const [wanted, setWanted] = useState(takeDestinationRequest);
  // Adjusted while rendering, once the rows are in (React's way for state that follows props).
  if (!wanted || rows === null) return;
  setWanted(null);
  const row = rows.find((r) => r.id === wanted);
  if (row && isAddable(row.kind)) setSheet({ kind: row.kind, existing: row });
}

const explanation =
  'Each confirmed document is packaged for download, then sent to every destination here.';

/** The kinds this Paige offers: every kind but a storage kind whose sign-in is not set up here. */
function useOfferedKinds(bridge: PaigeDestinationsBridge | undefined): PaigeDestinationKind[] {
  const [available, setAvailable] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!bridge) return;
    let live = true;
    bridge
      .storageProviders()
      .then(
        (providers) =>
          live && setAvailable(new Set(providers.filter((p) => p.available).map((p) => p.kind))),
      )
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [bridge]);
  return addableKinds.filter((kind) => !isStorageKind(kind) || available.has(kind));
}

/** The rows from Paige's bridge (reloaded on demand), and the test and retry actions that report in the note line. */
function useDestinations(bridge: PaigeDestinationsBridge | undefined) {
  const [rows, setRows] = useState<PaigeDestination[] | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);
  useEffect(() => {
    if (!bridge) return;
    let live = true;
    bridge
      .list()
      .then((listed) => live && setRows(listed))
      .catch(() => live && setRows(null));
    return () => {
      live = false;
    };
  }, [bridge, generation]);
  const reload = () => setGeneration((g) => g + 1);
  const act = async (action: DestinationAction, row: PaigeDestination) => {
    if (!bridge) return;
    setNote(startLine(action, row));
    try {
      setNote(await perform(bridge, action, row));
      reload();
    } catch (failure) {
      setNote(failure instanceof Error ? failure.message : 'Paige could not reach the destination');
    }
  };
  return { rows, note, setNote, reload, act };
}

function startLine(action: DestinationAction, row: PaigeDestination): string {
  if (action === 'test') return `Testing ${row.label}...`;
  if (action === 'retry') return `Retrying ${row.label}...`;
  return `Forgetting the trusted host key of ${row.label}...`;
}

/** The action's outcome in one line for the note; the rows reload after, so a newly trusted host key shows. */
async function perform(
  bridge: PaigeDestinationsBridge,
  action: DestinationAction,
  row: PaigeDestination,
): Promise<string> {
  if (action === 'test') return `${row.label}: ${(await bridge.test(row.id)).detail}`;
  if (action === 'reset-host-key') {
    await bridge.resetHostKey(row.id);
    return `${row.label}: the next test trusts the server's host key again`;
  }
  await bridge.retry(row.id);
  return `${row.label}: waiting deliveries are queued again`;
}

/** The card's line: Download only and what can be added here (the offered kinds), or how the rest are. */
function summaryLine(rows: PaigeDestination[], kinds: readonly PaigeDestinationKind[]): string {
  const others = rows.filter((r) => r.kind !== 'download');
  if (others.length === 0)
    return kinds.length > 0 ? `Download only; add ${offeredList(kinds)}` : 'Download only';
  const down = others.filter((r) => r.health === 'down').length;
  if (down > 0) return `${down} of ${others.length} destinations need attention`;
  return `Download and ${others.length} more`;
}
